import detectBrowserLanguage from 'detect-browser-language';
import includes from 'lodash/includes';
import {DISAMBIGUATION_QUESTIONS, AVA, siteTierConfig} from '../app.config';
import {store} from '../configureStore';
import log from 'loglevel';
import {
  isStorageAccessible,
  stringJoinWordsWithDashes,
  getLdUserExperimentName,
} from '.';
import '@adsk/dh-analytics/src/js/core/DHAnalytics';
import {getUserEntitlementLevel, getIsPowerUser} from '@adsk/ha-entitlements-util';
import {updateContainerEventMessage} from '../actions/uiActions';
import isEmpty from 'lodash/isEmpty';

const browserLang = detectBrowserLanguage();
const allowedEvents = [
  'cs_page_view',
  'cs_recommendation',
  'uhm_contact_support',
  'uhm_contact_support_feedback', //for contact support btn in feedback widget
  'skill_routing',
  'cs_modality',
  'cs_recommended_selected_article',
  'helpful_feedback',
  'helpful_rating',
  'helpful_comment',
  'helpful',
  'back_btn_clicked',
  'uhm_minimize_click',
  'uhm_unminimize_click',
  'uhm_close_click',
  'uhm_icon_click',
  'sales_chatbot_click',
  'initial_screen_page_view',
  'main_branch_selection',
  'ic_end_ques_selection',
  'ic_end_chat_click',
  'ic_end_chat',
  'ic_restart_chat',
  'uhm_home_click',
  'sub_branch_page_view',
  'ic_fallback',
  'ic_end_question',
  'restart_chat',
  'ic_fallback_selection',
  'uhm_loaded',
  'modality_click',
  'sales_phone_number_shown',
  'ic_save_chat_transcript_click',
  'give_feedback_click',
  'no_thanks_click',
  'uhm_dismiss_popup',
  'pop-up_shown',
  'pop-up_dismissed',
  'download_click',
  'export_control_message_shown',
];
const {appId, env, serviceUrl, apiKey, signWith} = siteTierConfig.adpConfigs;

export const adpAnalyticsInit = () => {
  window._dhaq = window._dhaq || [];
  const isSessionStorageAvailable = isStorageAccessible(() => sessionStorage);
  let trackingFlag = false;
  if (
    isSessionStorageAvailable &&
    sessionStorage.getItem('adsk_uhm_user_analytics_flag') === 'true'
  ) {
    trackingFlag = true;
  }
  let tmp = {
    cmd: 'init',
    config: {
      appId,
      env,
      serviceUrl,
      ADPData: {
        apiKey,
        signWith,
      },
      allowTracking: trackingFlag,
    },
  };
  window._dhaq.push(tmp);
};

/**
 * builds adp analytics cs_recommendation urls and sources
 *
 * @param {array} articles
 * @return {object} urls and sources
 **/
export const getAdpAnalyticsRecommendedArticles = (articles) => {
  try {
    return articles.reduce((acc, article, index) => {
      if (articles[index] !== 'undefined') {
        const articleUrl = article.url || article.contentUrl || '';
        const articleSource = article.source || '';
        return {
          ...acc,
          [`cm_cs_recommended_url${index + 1}`]: articleUrl,
          [`cm_cs_recommended_sl${index + 1}`]: articleSource,
        };
      } else return null;
    }, {});
  } catch (e) {
    console.warn('adpAnalyticsRecommendedArticles ERROR', e);
  }
};

/**
 * builds adp analytics payload
 *
 * @param {object} with the following fields:
 * akp_case_description -> cs user input
 * cm_cs_request_id
 * e -> adp event
 * ev -> adp event "value"
 * cm_cs_recommendation_action
 * otherFields (eg. cm_cs_recommended_articles, cm_skill_max_score_label)
 * @return {object} analytics payload
 **/
export const buildAdpAnalyticsPayload = ({
  cm_cs_request_id,
  akp_case_description,
  e,
  ev,
  cm_cs_recommendation_action,
  caseNumber,
  ...otherFields
}) => {
  const {
    uiOptions: {
      componentShowingInUH,
      componentData: {engLabelforAnalytics} = {},
      selectedProduct = {},
    },
    workflowPrompts: {
      seenGuids = [],
      currentPrompt: {engLabelforAnalytics: currentPrompLabel} = {},
      workflowData: {prompts = {}} = {},
    },
    appSettings: {
      country,
      pageTitle,
      pageUrl,
      language,
      utmSource,
      utmSourceOverride,
      pageReferrer,
      flowId,
      productDetails,
      release,
      integrationType,
    },
    userData: {blockedCSN},
  } = store.getState();
  const {value: p = productDetails?.value ?? ''} = selectedProduct || {}; //{} prevents TypeError when selectedProduct is null

  //properties w/ undefined vals get removed
  let uhmBranchTreeAnalytics = [];
  const branchAnalytics = {};
  if (!isEmpty(seenGuids) && !isEmpty(prompts)) {
    uhmBranchTreeAnalytics = seenGuids.reduce((crums, guidValue, index) => {
      if (index > 0) {
        crums.push(prompts[guidValue].engLabelforAnalytics);
      }
      return crums;
    }, []);

    if (uhmBranchTreeAnalytics.length > 0) {
      let currentBranch;
      if (componentShowingInUH === DISAMBIGUATION_QUESTIONS) {
        currentBranch = currentPrompLabel;
      }
      const [cm_cs_branch_path, cm_cs_sub_branch, cm_cs_sub_branch_2] = [
        ...new Set([...uhmBranchTreeAnalytics, currentBranch]),
      ];
      branchAnalytics['cm_cs_branch_path'] = cm_cs_branch_path;
      branchAnalytics['cm_cs_sub_branch'] = cm_cs_sub_branch;
      branchAnalytics['cm_cs_sub_branch_2'] = cm_cs_sub_branch_2;
    }
  }

  const payload = {
    akp_case_description,
    e,
    cm_cs_recommendation_action,
    p,
    ev: ev || stringJoinWordsWithDashes(engLabelforAnalytics),
    cm_cs_topic_title: engLabelforAnalytics,
    c: country,
    t: pageTitle,
    u: pageUrl,
    l: language,
    cm_current_app:
      componentShowingInUH === AVA && utmSourceOverride
        ? utmSourceOverride
        : utmSource,
    cm_origin_app: utmSourceOverride ? utmSourceOverride : utmSource,
    cm_flow_id: flowId,
    ru: pageReferrer,
    ...(cm_cs_request_id && {cm_cs_request_id}),
    ...(blockedCSN && {cm_blocked_csn: blockedCSN}),
    ...otherFields,
    ...branchAnalytics,
    cm_launchdarkly: getLdUserExperimentName(),
    cm_case_id: caseNumber,
    r: release,
    ...(integrationType === 'In-Product' && {cm_experience: 'Legacy'}),
  };

  return payload;
};

// set values
// see value definitions:
// https://wiki.autodesk.com/pages/viewpage.action?spaceKey=BEEHIVE&title=AKN+Analytics+ADP+Services
/**
 * triggers adp analytics logs
 *
 * @param {object} with the following fields:
 * e -> adp event
 * ev -> adp event "value"
 * otherParams: any other field returned by the buildAdpAnalyticsPayload function
 * @return {undefined}
 **/
export const adpAnalyticsLog = ({e, ev, ...otherParams}) => {
  if (
    typeof e === 'undefined' ||
    e.length === 0 ||
    !includes(allowedEvents, e)
  ) {
    log.warn(`event ${e} is not setup for ADP`);
    return false;
  }

  if (typeof ev === 'undefined' || ev.length === 0) {
    log.warn('event value not provided in ADP call');
    return false;
  }

  // set up the base data object
  let data = {
    bl: browserLang,
    e: e,
    ev: ev,
  };

  let adpData = {...data, ...otherParams};
  let getStore = store.getState();
  let getCurrentUser = getStore.userData.userDetails;
  let getCurrentUserAnalyticsId = getStore.userData.analyticsId;
  let userEntitlements = getStore.userData.userEntitlements;
  let userContext = getStore.workflowPrompts.userContext;
  let getUserMcvisid = getStore.userData.mcvisid;

  const entitlement = getUserEntitlementLevel(userEntitlements);
  const isPowerUser = getIsPowerUser(entitlement);

  adpData.cm_aknuserrole = `${entitlement}${isPowerUser ? ' - Power User' : ''}`;
  adpData.cm_edubranch = userContext === 'Edu';

  if (getCurrentUser && getCurrentUser.userid) {
    adpData.cu = getCurrentUser.userid;
  }

  if (getCurrentUserAnalyticsId) {
    adpData.ui = getCurrentUserAnalyticsId;
  }

  if (getUserMcvisid) {
    adpData.cm_mcvisid = getUserMcvisid;
  }

  log.debug('adpData: ', adpData);

  window._dhaq.push({
    cmd: 'log',
    event: data.e,
    value: data.ev,
    data: adpData,
  });
};

export const getRecommendationAction = (type, action) => {
  let recommendedAction = 'not_applicable';

  if (type === 'suggested' || type === 'searched') {
    if (action === '') {
      recommendedAction = 'skip';
    } else if (action === 'click') {
      recommendedAction = 'click_ignored';
    } else {
      recommendedAction = action;
    }
  }

  return recommendedAction;
};

//uhm_contact_support_feedback if triggered in fback wdgt
export const adpContactSupportLink = (
  requestId,
  eventName = 'uhm_contact_support'
) => {
  const contentSuggestionInput =
    store?.getState().uiOptions?.contentSuggestionInput;
  const {
    analytics: {
      recommendedLinksAdpData: {
        cm_cs_recommended_url_click_sl,
        cm_cs_recommended_url_click,
        akp_case_description,
        cm_cs_recommended_article_type,
        cm_cs_recommendation_action,
      } = {},
    } = {},
  } = store.getState();

  const csOnlyPayload = contentSuggestionInput
    ? {
        akp_case_description,
        cm_cs_recommendation_action: getRecommendationAction(
          cm_cs_recommended_article_type,
          cm_cs_recommendation_action
        ),
        cm_cs_recommended_url_click_sl,
        cm_cs_recommended_url_click,
      }
    : {};

  adpAnalyticsLog(
    buildAdpAnalyticsPayload({
      cm_cs_request_id: requestId,
      e: eventName,
      ev: '1',
      ...csOnlyPayload,
    })
  );
};

/**
 * triggers Fusion UHMShow, page view adp events
 *
 * @param {string} requestId generated in Content Suggestions
 * @return {undefined}
 **/
export const adpFusionUhmShowEvents = (requestId) => {
  adpAnalyticsLog(
    buildAdpAnalyticsPayload({
      e: 'uhm_icon_click',
      ev: true,
      cm_cs_request_id: requestId,
    })
  );

  adpAnalyticsLog(
    buildAdpAnalyticsPayload({
      e: 'cs_page_view',
      ev: true,
      cm_cs_request_id: requestId,
    })
  );

  //resets containerEventMessage
  store.dispatch(updateContainerEventMessage(''));
};

/**
 * Analytics Metadata passed to other components.
 */

export const getAnalyticsMetadata = () => {
  const {
    uiOptions: {componentShowingInUH, selectedProduct = {}, requestId},
    workflowPrompts: {
      currentTopLevelBranch,
      currentPrompt: {engLabelforAnalytics} = {},
      userContext,
      sfdcType,
      sfdcTopic,
      defaultSupportSkill,
      resolvedSkill,
    },
    appSettings: {
      country,
      pageTitle,
      pageUrl,
      language,
      utmSource,
      utmSourceOverride,
      pageReferrer,
      flowId,
      origin,
      subOrigin,
      productDetails,
      release,
      integrationType,
    },
    analytics: {recommendedLinksAdpData},
    userData: {blockedCSN},
  } = store.getState();

  let cm_cs_recommendation_action;
  if (!isEmpty(recommendedLinksAdpData)) {
    let {cm_cs_recommendation_action = '', cm_cs_recommended_article_type} =
      recommendedLinksAdpData;
    cm_cs_recommendation_action = getRecommendationAction(
      cm_cs_recommended_article_type,
      cm_cs_recommendation_action
    );
  }
  const isSessionStorageAvailable = isStorageAccessible(() => sessionStorage);

  const analyticsMetaData = {
    currentTopLevelBranch,
    engLabelforAnalytics,
    userContext,
    type: sfdcType,
    topic: sfdcTopic,
    selectedProduct: selectedProduct
      ? selectedProduct
      : {productCode: productDetails.value},
    productDetails: {
      productName: productDetails.label,
      productCode: productDetails.value,
      release:
        release ??
        productDetails.releases?.[productDetails.releases.length - 1],
    },
    release,
    country,
    pageTitle,
    pageUrl,
    language,
    utmSource,
    utmSourceOverride,
    pageReferrer,
    flowId,
    origin,
    subOrigin,
    cm_current_app:
      componentShowingInUH === AVA && utmSourceOverride
        ? utmSourceOverride
        : utmSource,
    cm_origin_app: utmSourceOverride ? utmSourceOverride : utmSource,
    cm_flow_id: flowId,
    cm_cs_request_id: requestId,
    cm_cs_recommendation_action,
    blockedCSN: blockedCSN,
    trackingFlag:
      isSessionStorageAvailable &&
      sessionStorage.getItem('adsk_uhm_user_analytics_flag') &&
      sessionStorage.getItem('adsk_uhm_user_analytics_flag') === 'true'
        ? true
        : false,
    launchExperiment: getLdUserExperimentName(),
    skillIntent:
      resolvedSkill && resolvedSkill !== ''
        ? resolvedSkill
        : defaultSupportSkill,
    ...(integrationType === 'In-Product' && {cm_experience: 'Legacy'}),
  };
  return analyticsMetaData;
};
